<!-- 人员状态统计 — 图表 -->
<template>
  <div class="charts-section" v-loading="loading">
    <el-row :gutter="20">
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">工单/任务状态分布</span>
          </div>
          <div class="chart-container" ref="statusChart"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">班组负荷（子任务条数）</span>
          </div>
          <div class="chart-container" ref="teamChart"></div>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="20" style="margin-top: 20px;">
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">人均完成趋势（按主单创建日）</span>
          </div>
          <div class="chart-container" ref="dailyChart"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">TOP10 完成主单（责任人）</span>
          </div>
          <div class="chart-container" ref="topChart"></div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'PersonnelChartsSection',
  props: {
    statusList: { type: Array, default: () => [] },
    teamLoad: { type: Array, default: () => [] },
    dailyList: { type: Array, default: () => [] },
    topList: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data() {
    return { charts: {} };
  },
  mounted() {
    this.$nextTick(() => this.initCharts());
  },
  watch: {
    statusList: { handler() { this.updateStatus(); }, deep: true },
    teamLoad: { handler() { this.updateTeam(); }, deep: true },
    dailyList: { handler() { this.updateDaily(); }, deep: true },
    topList: { handler() { this.updateTop(); }, deep: true }
  },
  methods: {
    initCharts() {
      if (this.$refs.statusChart) {
        this.charts.status = echarts.init(this.$refs.statusChart);
        this.updateStatus();
      }
      if (this.$refs.teamChart) {
        this.charts.team = echarts.init(this.$refs.teamChart);
        this.updateTeam();
      }
      if (this.$refs.dailyChart) {
        this.charts.daily = echarts.init(this.$refs.dailyChart);
        this.updateDaily();
      }
      if (this.$refs.topChart) {
        this.charts.top = echarts.init(this.$refs.topChart);
        this.updateTop();
      }
      window.addEventListener('resize', this.onResize);
    },
    onResize() {
      Object.values(this.charts).forEach(c => c && c.resize());
    },
    updateStatus() {
      if (!this.charts.status) return;
      const data = (this.statusList || []).map(item => ({
        value: item.count || 0,
        name: item.statusName || '—'
      }));
      this.charts.status.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { orient: 'vertical', left: 'left', type: 'scroll' },
        series: [{
          name: '状态',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['58%', '50%'],
          data: data.length ? data : [{ value: 0, name: '暂无数据' }],
          label: { formatter: '{b}: {c}' }
        }]
      });
    },
    updateTeam() {
      if (!this.charts.team) return;
      const teams = (this.teamLoad || []).map(t => t.teamName || '未分班');
      const run = (this.teamLoad || []).map(t => t.runningExtCount || 0);
      const pend = (this.teamLoad || []).map(t => t.pendingExtCount || 0);
      const done = (this.teamLoad || []).map(t => t.completedExtCount || 0);
      this.charts.team.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['执行中子任务', '待执行子任务', '已完成子任务'] },
        xAxis: { type: 'category', data: teams, axisLabel: { rotate: teams.some(x => x.length > 6) ? 30 : 0 } },
        yAxis: { type: 'value', name: '条数' },
        series: [
          { name: '执行中子任务', type: 'bar', data: run, itemStyle: { color: '#409eff' } },
          { name: '待执行子任务', type: 'bar', data: pend, itemStyle: { color: '#e6a23c' } },
          { name: '已完成子任务', type: 'bar', data: done, itemStyle: { color: '#67c23a' } }
        ]
      });
    },
    updateDaily() {
      if (!this.charts.daily) return;
      const dates = (this.dailyList || []).map(d => (d.statDate || '').split('-').slice(1).join('-'));
      const mainAvg = (this.dailyList || []).map(d => Number(d.avgMainPerPerson || 0));
      const extAvg = (this.dailyList || []).map(d => Number(d.avgExtPerPerson || 0));
      this.charts.daily.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['人均完成主单', '人均完成子任务点位'] },
        xAxis: { type: 'category', data: dates },
        yAxis: { type: 'value', name: '件/人' },
        series: [
          { name: '人均完成主单', type: 'line', smooth: true, data: mainAvg, itemStyle: { color: '#e6a23c' } },
          { name: '人均完成子任务点位', type: 'line', smooth: true, data: extAvg, itemStyle: { color: '#409eff' } }
        ]
      });
    },
    updateTop() {
      if (!this.charts.top) return;
      const list = [...(this.topList || [])].sort((a, b) => (a.completedMainCount || 0) - (b.completedMainCount || 0)).slice(-10);
      const names = list.map(x => x.realName || String(x.userId || ''));
      const vals = list.map(x => x.completedMainCount || 0);
      this.charts.top.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '8%', bottom: '3%', containLabel: true },
        xAxis: { type: 'value', name: '主单数' },
        yAxis: { type: 'category', data: names, inverse: true, axisLabel: { fontSize: 11 } },
        series: [{
          name: '完成主单',
          type: 'bar',
          data: vals,
          itemStyle: { color: '#409eff' }
        }]
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    Object.values(this.charts).forEach(c => c && c.dispose());
  }
};
</script>

<style lang="scss" scoped>
.charts-section {
  .chart-card {
    height: 400px;
    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .chart-title { font-size: 16px; font-weight: 600; color: #303133; }
    }
    .chart-container { width: 100%; height: 300px; }
  }
}
</style>
